import styled, { css } from 'styled-components'
import { Box } from '../../shared_components/UI/Box'
import { getRgbaValue } from '../../utils/styling'
import Slider from 'react-infinite-logo-slider'
import { createClipPathByChunkSize } from '../Dashboard/Vault/styles'

const hiddenScroll = css`
  scrollbar-width: none;
  &::-webkit-scrollbar {
    display: none;
  }
`

const Container = styled(Box)`
  position: relative;
`

const OuterContainer = styled(Box)`
  overflow: scroll;
  position: relative;
  scroll-snap-type: x mandatory;
  ${hiddenScroll}
  a {
    min-width: max-content;
  }
`

const Subtitle = styled.h3`
  color: #fff;
  text-align: center;
  font-feature-settings: 'ss01' on;
  font-family: 'Space Grotesk';
  font-size: 32px;
  font-style: normal;
  font-weight: 300;
  line-height: 25px; /* 78.125% */
  letter-spacing: 9.6px;
  text-transform: uppercase;
  line-height: 1.5;
  strong {
    font-weight: 700;
  }
`

const GradientLeft = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 10;
  background: linear-gradient(
    90deg,
    ${({ theme }) => getRgbaValue(theme.semantics.zap.surface.primary, 1)} 5%,
    transparent 20%,
    transparent 100%
  );
  @media screen and (max-width: 768px) {
    background: linear-gradient(
      90deg,
      ${({ theme }) => getRgbaValue(theme.semantics.zap.surface.primary, 1)}
        2.5%,
      transparent 10%,
      transparent 100%
    );
  }
  pointer-events: none;
`

const GradientRight = styled(Box)`
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 10;
  right: 0;
  background: linear-gradient(
    90deg,
    transparent 0%,
    transparent 80%,
    ${({ theme }) => getRgbaValue(theme.semantics.zap.surface.primary, 1)} 95%
  );
  pointer-events: none;
  @media screen and (max-width: 768px) {
    background: linear-gradient(
      90deg,
      transparent 0%,
      transparent 90%,
      ${({ theme }) => getRgbaValue(theme.semantics.zap.surface.primary, 1)}
        97.5%
    );
  }
`

const OurBackers = () => {
  return (
    <Box dc g={80} c>
      <Subtitle>
        Working with the <strong>best</strong>
      </Subtitle>
      <Box pr dc g={80}>
        <GradientLeft />
        <OuterContainer>
          <Container dc g={16}>
            <VCs />
          </Container>
        </OuterContainer>
        <OuterContainer>
          <Container dc g={16}>
            <Angels />
          </Container>
        </OuterContainer>
        <GradientRight />
      </Box>
    </Box>
  )
}

const CURRENT_VCS = [
  {
    name: 'Sharding Capital',
    link: 'https://sharding.capital',
    logo: '/img/backers/vcs/sharding.png',
    imageClassname: 'sharding',
  },
  {
    name: 'Presto Labs',
    link: 'https://www.prestolabs.io',
    logo: '/img/backers/vcs/presto.svg',
  },
  {
    name: 'Cypher Capital',
    link: 'https://cyphercapital.com',
    logo: '/img/backers/vcs/cypher.png',
    imageClassname: 'cypher',
  },
  {
    name: 'Rarestone Capital',
    link: 'https://www.rarestone.ventures',
    logo: '/img/backers/vcs/rarestone.svg',
    imageClassname: 'rarestone',
  },
]

const CURRENT_ANGELS = [
  {
    name: 'Larry Cermak',
    link: 'https://twitter.com/lawmaster',
    logo: '/img/backers/angels/larry.jpeg',
  },
  {
    name: 'Luca Netz',
    link: 'https://twitter.com/LucaNetz',
    logo: '/img/backers/angels/luca.jpeg',
  },
  {
    name: 'Ice Bagz',
    link: 'https://twitter.com/icebagz_',
    logo: '/img/backers/angels/ice.jpeg',
  },
  {
    name: 'Zeneca',
    link: 'https://twitter.com/Zeneca',
    logo: '/img/backers/angels/zeneca.jpeg',
  },
]

function Angels() {
  const vcs = CURRENT_ANGELS
  return (
    <Slider width="250px" duration={20} pauseOnHover={true} blurBorders={false}>
      {vcs.map(({ name, link, logo }) => (
        <Slider.Slide key={name}>
          <a href={link} target="_blank" rel="noreferrer noopeneer">
            <AngelCard dc className="vc-card" c g={16}>
              <AngelImageWrapper c>
                <img src={logo} alt={name} />
              </AngelImageWrapper>
              <span>{name}</span>
            </AngelCard>
          </a>
        </Slider.Slide>
      ))}
    </Slider>
  )
}

export const AngelImageWrapper = styled(Box)`
  width: 212px;
  height: 150px;
  overflow: hidden;
  position: relative;
  img {
    position: absolute;
    top: auto;
    bottom: auto;
  }
`

export const AngelCard = styled(Box)`
  min-height: 200px;
  min-width: max-content;
  scroll-snap-align: center;
  img {
    width: 212px;
  }
  span {
    color: #fff;
    text-align: center;
    font-feature-settings: 'ss01' on;
    font-family: 'Space Grotesk';
    font-size: 24px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
  }
`

const VcCard = styled(Box)`
  background: #131f29;
  border: 1px solid #131f29;
  padding: 0 2.5rem;
  min-height: 84px;
  min-width: 320px;
  align-items: center;
  justify-content: center;
  scroll-snap-align: center;
  img.rarestone {
    min-height: 28px;
    min-width: 192px;
    filter: invert(1);
  }
  img.cypher {
    max-width: 192px;
    filter: invert(1);
  }

  img.sharding {
    max-width: 192px;
  }

  ${createClipPathByChunkSize(12)}
`

function VCs() {
  const vcs = CURRENT_VCS

  return (
    <Slider
      width="340px"
      duration={15}
      pauseOnHover={true}
      blurBorders={false}
      toRight
    >
      {vcs.map(({ name, link, logo, imageClassname }) => (
        <Slider.Slide key={name}>
          <a href={link} target="_blank" rel="noreferrer noopeneer">
            <VcCard key={name} className="vc-card">
              <img src={logo} alt={name} className={imageClassname} />
            </VcCard>
          </a>
        </Slider.Slide>
      ))}
    </Slider>
  )
}

export default OurBackers
